import React, { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { useSite } from "@/context/SiteContext";
import Seo from "@/components/Seo";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const FIELDS = [
  ["business_name", "Business Name"],
  ["whatsapp_number", "WhatsApp Number (with country code, e.g. 447561556057)"],
  ["phone", "Phone Number"],
  ["email", "Email"],
  ["address", "Business Address"],
  ["service_area", "Manchester Service Area"],
  ["opening_hours", "Opening Hours"],
  ["company_number", "Company Number"],
  ["homepage_headline", "Homepage Headline"],
];

export default function Settings() {
  const { refresh } = useSite();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
      refresh();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!form) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-zinc-400" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <Seo title="Website Settings" noindex path="/admin/settings" />
      <h1 className="font-heading text-3xl font-black tracking-tight">Website Settings</h1>
      <p className="mt-2 text-sm text-zinc-500">Changing the WhatsApp number updates every WhatsApp button across the site.</p>

      <div className="mt-6 grid gap-4 border border-border bg-white p-6 sm:grid-cols-2" data-testid="settings-form">
        {FIELDS.map(([k, l]) => (
          <div key={k}>
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{l}</Label>
            <Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`setting-${k}`} className="mt-1 h-11" />
          </div>
        ))}
        <div className="sm:col-span-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Homepage Short Text</Label>
          <Textarea rows={3} value={form.homepage_subtext || ""} onChange={(e) => set("homepage_subtext", e.target.value)} data-testid="setting-subtext" className="mt-1" />
        </div>
      </div>

      <div className="mt-6">
        <button onClick={save} disabled={saving} data-testid="save-settings" className="inline-flex h-12 items-center gap-2 rounded-full bg-[#00E676] px-8 font-bold text-black disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Settings
        </button>
      </div>
    </AdminLayout>
  );
}
